const DEFAULT_LIFECYCLE_STAGES = {
    uninitialized: "uninitialized",
    ready: "ready",
    invalidated: "invalidated",
};
/**
 * Serializes commands for a lazily initialized handle.
 * Each command payload is built only when its turn runs, so it always sees
 * the latest settled handle id from prior commands.
 */
const createLazyHandleClientWithStages = (input) => {
    let handleId;
    let externallyInvalidatedHandleId;
    let tail = Promise.resolve();
    let queuedCommandCount = 0;
    let epoch = 0;
    let activeCommand;
    const { stages } = input;
    let stage = input.initialStage ?? stages.uninitialized;
    const subscribers = new Set();
    const stageSubscribers = new Set();
    const enqueue = (execute) => {
        const run = queuedCommandCount === 0 ? execute() : tail.then(execute, execute);
        queuedCommandCount += 1;
        tail = run.then(() => {
            queuedCommandCount -= 1;
        }, () => {
            queuedCommandCount -= 1;
        });
        return run;
    };
    const setStage = (nextStage) => {
        if (stage === nextStage) {
            return;
        }
        if (stage === stages.invalidated && nextStage !== stages.invalidated) {
            return;
        }
        stage = nextStage;
        stageSubscribers.forEach((subscriber) => {
            subscriber(nextStage);
        });
    };
    const publishHandle = (nextHandleId, command) => {
        if (stage === stages.invalidated) {
            return;
        }
        if (nextHandleId === externallyInvalidatedHandleId ||
            command.invalidatedHandleIds.has(nextHandleId)) {
            return;
        }
        externallyInvalidatedHandleId = undefined;
        handleId = nextHandleId;
        setStage(stages.ready);
        subscribers.forEach((subscriber) => {
            subscriber(nextHandleId);
        });
    };
    const publishHandleFromSettlement = (settlement, command) => {
        const settledHandleId = input.getHandleIdFromSettlement(settlement);
        if (settledHandleId && command.epoch === epoch) {
            publishHandle(settledHandleId, command);
        }
    };
    const runCommand = (buildPayload, dispatchAndWait) => {
        const execute = async () => {
            if (stage === stages.invalidated) {
                throw new Error("Handle client is invalidated");
            }
            const command = {
                epoch,
                invalidatedHandleIds: new Set(),
            };
            activeCommand = command;
            try {
                const payload = buildPayload(handleId);
                const settlement = await dispatchAndWait(payload);
                publishHandleFromSettlement(settlement, command);
                return settlement;
            }
            finally {
                if (activeCommand === command) {
                    activeCommand = undefined;
                }
            }
        };
        return enqueue(execute);
    };
    const runCommandWithRetry = (buildPayload, dispatchAndWait, options) => {
        const execute = async () => {
            if (stage === stages.invalidated) {
                throw new Error("Handle client is invalidated");
            }
            const command = {
                epoch,
                invalidatedHandleIds: new Set(),
            };
            activeCommand = command;
            try {
                let didRetry = false;
                for (;;) {
                    if (stage === stages.invalidated) {
                        throw new Error("Handle client is invalidated");
                    }
                    const payload = buildPayload(handleId);
                    try {
                        const settlement = await dispatchAndWait(payload);
                        publishHandleFromSettlement(settlement, command);
                        return settlement;
                    }
                    catch (error) {
                        if (command.epoch === epoch &&
                            !didRetry &&
                            options.shouldRetry(error)) {
                            didRetry = true;
                            options.onBeforeRetry?.(error);
                            if (activeCommand !== command ||
                                command.epoch !== epoch ||
                                stage === stages.invalidated) {
                                throw error;
                            }
                            continue;
                        }
                        throw error;
                    }
                }
            }
            finally {
                if (activeCommand === command) {
                    activeCommand = undefined;
                }
            }
        };
        return enqueue(execute);
    };
    const runResolvedHandleCommand = (buildPayload, dispatchAndWait) => enqueue(async () => {
        if (stage === stages.invalidated) {
            throw new Error("Handle client is invalidated");
        }
        if (handleId === undefined) {
            return;
        }
        await dispatchAndWait(buildPayload(handleId));
    });
    const clearHandleIfMatching = (expectedHandleId) => {
        if (handleId !== expectedHandleId) {
            return undefined;
        }
        const previousHandleId = handleId;
        handleId = undefined;
        externallyInvalidatedHandleId = previousHandleId;
        setStage(stages.uninitialized);
        return previousHandleId;
    };
    const recordHandleInvalidation = (invalidatedHandleId) => {
        if (activeCommand?.epoch === epoch) {
            activeCommand.invalidatedHandleIds.add(invalidatedHandleId);
        }
        return clearHandleIfMatching(invalidatedHandleId);
    };
    const clearHandleForRetry = () => {
        if (!handleId) {
            return undefined;
        }
        const previousHandleId = handleId;
        handleId = undefined;
        externallyInvalidatedHandleId = undefined;
        setStage(stages.uninitialized);
        return previousHandleId;
    };
    return {
        runCommand,
        runCommandWithRetry,
        runResolvedHandleCommand,
        getHandleId: () => handleId,
        clearHandleForRetry,
        recordHandleInvalidation,
        clearHandleIfMatching,
        hasPendingCommands: () => queuedCommandCount > 0,
        subscribeHandle: (subscriber) => {
            subscribers.add(subscriber);
            if (handleId) {
                subscriber(handleId);
            }
            return () => {
                subscribers.delete(subscriber);
            };
        },
        getStage: () => stage,
        setStage,
        subscribeStage: (subscriber) => {
            stageSubscribers.add(subscriber);
            subscriber(stage);
            return () => {
                stageSubscribers.delete(subscriber);
            };
        },
        reset: () => {
            epoch += 1;
            activeCommand = undefined;
            handleId = undefined;
            externallyInvalidatedHandleId = undefined;
            stage = stages.uninitialized;
            stageSubscribers.forEach((subscriber) => {
                subscriber(stage);
            });
        },
        invalidate: () => {
            epoch += 1;
            activeCommand = undefined;
            handleId = undefined;
            externallyInvalidatedHandleId = undefined;
            setStage(stages.invalidated);
        },
    };
};
export function createLazyHandleClient(options) {
    if ("stages" in options && options.stages) {
        return createLazyHandleClientWithStages({
            getHandleIdFromSettlement: options.getHandleIdFromSettlement,
            stages: options.stages,
            initialStage: options.initialStage,
        });
    }
    return createLazyHandleClientWithStages({
        getHandleIdFromSettlement: options.getHandleIdFromSettlement,
        stages: DEFAULT_LIFECYCLE_STAGES,
        initialStage: options.initialStage,
    });
}
